import React from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FieldDiff, RefreshType, SurveyDiffResult } from '@features/survey-templates/types';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { SURVEY } from '@constants/terminology';

interface RefreshConfirmationModalProps {
  modifiedSurveys: SurveyDiffResult[];
  refreshType: RefreshType;
  isRefreshing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const getRefreshLabel = (type: RefreshType) => {
  switch (type) {
    case RefreshType.Scope:
      return 'scope';
    case RefreshType.Workgroup:
      return 'delegated workgroups';
    case RefreshType.Settings:
      return `${SURVEY.SINGULAR} settings`;
    default:
      return type;
  }
};

export const RefreshConfirmationModal: React.FC<RefreshConfirmationModalProps> = ({
  modifiedSurveys,
  refreshType,
  isRefreshing,
  onConfirm,
  onCancel,
}) => {
  const refreshLabel = getRefreshLabel(refreshType);

  return (
    <Modal isOpen={true} toggle={onCancel} size='lg' scrollable>
      <ModalHeader toggle={onCancel}>Confirm refresh {refreshLabel}</ModalHeader>
      <ModalBody>
        <p>
          The following {SURVEY.PLURAL} have been modified since their creation. Are you sure you want to refresh them?
        </p>
        <ul>
          {modifiedSurveys.map((survey) => (
            <li key={survey.surveyId} className='mb-3'>
              <Button
                color='link'
                href={generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
                  initiativeId: survey.initiativeId,
                  surveyId: survey.surveyId,
                  page: 'overview',
                })}
                target='_blank'
                rel='noopener noreferrer'
              >
                {survey.initiativeName} - {survey.surveyName}
              </Button>
              {refreshType === RefreshType.Settings && (
                <ul className='mt-1'>
                  {survey.diffs.map((diff: FieldDiff) => (
                    <li key={diff.field}>{diff.label}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' onClick={onCancel} disabled={isRefreshing}>
          Cancel
        </Button>
        <Button color='primary' onClick={onConfirm} disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing...' : 'Confirm Refresh'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
